import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Briefcase, Hand, ArrowRight, Check,
  Zap, Globe2, ShieldCheck,
} from 'lucide-react';
import '../styles/landing.css';

/**
 * Landing — Phase 14 public entry at `/`.
 *
 * Hero with two clear CTA cards (Recruiter B2B vs Consumer translator) so
 * new visitors self-select instead of dropping into the detection app. The
 * TopNav still renders above (via App.js) — this page contributes only the
 * scrollable content below the 56px spacer.
 *
 * The page is 100% static presentation. No data fetch, no side effects
 * beyond an IntersectionObserver that adds `.is-visible` to sections for
 * the fade-up-on-scroll animation.
 */

export default function Landing() {
  const rootRef = useRef(null);

  // Progressive reveal for below-the-fold blocks. `prefers-reduced-motion`
  // users get the content immediately (no observer, no transforms).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      rootRef.current?.querySelectorAll('.lp-reveal').forEach((el) => {
        el.classList.add('is-visible');
      });
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 },
    );
    rootRef.current?.querySelectorAll('.lp-reveal').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="lp-shell" ref={rootRef} data-testid="landing-page">
      {/* Ambient background grid + gradient wash */}
      <div className="lp-bg-grid" aria-hidden="true" />
      <div className="lp-bg-glow lp-bg-glow--cyan" aria-hidden="true" />
      <div className="lp-bg-glow lp-bg-glow--violet" aria-hidden="true" />

      {/* HERO */}
      <section className="lp-hero" data-testid="landing-hero">
        <p className="lp-eyebrow lp-reveal">
          <span className="lp-eyebrow-dot" aria-hidden="true" />
          Inclusive hiring · Powered by AI
        </p>
        <h1 className="lp-headline lp-reveal" data-testid="landing-headline">
          Break the silence barrier.
          <br />
          <span className="lp-headline-accent">Hire inclusively.</span>
        </h1>
        <p className="lp-subheadline lp-reveal">
          SignVoice&apos;s AI-powered platform helps deaf candidates and
          recruiters connect through real-time sign language translation.
        </p>

        {/* CTA cards */}
        <div className="lp-cta-grid lp-reveal" data-testid="landing-cta-grid">
          {/* Recruiter card — cyan / Business accent */}
          <Link
            to="/talent-connect"
            className="lp-cta-card lp-cta-card--recruiter"
            data-testid="landing-cta-recruiter"
          >
            <span className="lp-cta-badge lp-cta-badge--business">Business</span>
            <span className="lp-cta-icon lp-cta-icon--cyan" aria-hidden="true">
              <Briefcase size={26} strokeWidth={1.8} />
            </span>
            <h2 className="lp-cta-title">For Recruiters &amp; Enterprise</h2>
            <p className="lp-cta-desc">
              B2B interview platform for staffing agencies. Pre-qualify deaf
              candidates with AI-powered async interviews.
            </p>
            <span className="lp-trial-pill" data-testid="landing-trial-pill">
              ✨ 30-day free trial · no credit card
            </span>
            <ul className="lp-cta-features" data-testid="landing-features-recruiter">
              <li><Check size={14} strokeWidth={2.5} /> Live analytics dashboard</li>
              <li><Check size={14} strokeWidth={2.5} /> Custom question sets</li>
              <li><Check size={14} strokeWidth={2.5} /> Bilingual AI assistant (EN/ES)</li>
            </ul>
            <span className="lp-cta-btn lp-cta-btn--cyan">
              Launch Recruiter Portal
              <ArrowRight size={16} strokeWidth={2.2} />
            </span>
          </Link>

          {/* Translator card — violet / Free accent */}
          <Link
            to="/signvoice"
            className="lp-cta-card lp-cta-card--translator"
            data-testid="landing-cta-translator"
          >
            <span className="lp-cta-badge lp-cta-badge--free">Free</span>
            <span className="lp-cta-icon lp-cta-icon--violet" aria-hidden="true">
              <Hand size={26} strokeWidth={1.8} />
            </span>
            <h2 className="lp-cta-title">Accessibility SignVoice Translator</h2>
            <p className="lp-cta-desc">
              Free real-time American Sign Language to voice translation.
              Works entirely on your device — no data uploads, ever.
            </p>
            <ul className="lp-cta-features" data-testid="landing-features-translator">
              <li><Check size={14} strokeWidth={2.5} /> 14 ASL signs supported</li>
              <li><Check size={14} strokeWidth={2.5} /> Dual-hand tracking</li>
              <li><Check size={14} strokeWidth={2.5} /> 100% on-device (privacy-first)</li>
            </ul>
            <span className="lp-cta-btn lp-cta-btn--violet">
              Try the Translator
              <ArrowRight size={16} strokeWidth={2.2} />
            </span>
          </Link>
        </div>
      </section>

      {/* PILLARS */}
      <section className="lp-pillars lp-reveal" data-testid="landing-pillars">
        <div className="lp-pillar">
          <span className="lp-pillar-icon" aria-hidden="true"><Zap size={20} /></span>
          <h3 className="lp-pillar-title">Real-time detection</h3>
          <p className="lp-pillar-copy">
            MediaPipe-powered dual-hand tracking runs at 30fps. Signs become
            speech in under 200ms, no server round-trip.
          </p>
        </div>
        <div className="lp-pillar">
          <span className="lp-pillar-icon" aria-hidden="true"><Globe2 size={20} /></span>
          <h3 className="lp-pillar-title">Bilingual assistant</h3>
          <p className="lp-pillar-copy">
            Claude Sonnet 4.5 answers recruiter questions in English or
            Spanish with live analytics context — no rigid FAQs.
          </p>
        </div>
        <div className="lp-pillar">
          <span className="lp-pillar-icon" aria-hidden="true"><ShieldCheck size={20} /></span>
          <h3 className="lp-pillar-title">Enterprise-ready</h3>
          <p className="lp-pillar-copy">
            SlowAPI rate limits, host-aware CORS, HSTS, and MongoDB
            aggregation baked in. Ready for staffing-agency scale.
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer" data-testid="landing-footer">
        <div className="lp-footer-copyright">
          © 2026 SignVoice · signvoicetalentconnect.agency
        </div>
        <nav className="lp-footer-links" aria-label="Footer">
          <Link to="/privacy" data-testid="landing-footer-privacy">Privacy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms" data-testid="landing-footer-terms">Terms</Link>
          <span aria-hidden="true">·</span>
          <a
            href="mailto:hello@signvoicetalentconnect.agency"
            data-testid="landing-footer-contact"
          >Contact</a>
        </nav>
      </footer>
    </div>
  );
}
